// Terms & Policies widget: data-365="policies"
// The venue's "Terms & Policies" page: who the seller is, sales tax, booking terms and house rules, the
// booking refund rule, how the venue's rules sit on top of 365's Terms of Service + Privacy Policy, and
// contact details. The text is GENERATED server-side from the venue record and its refund settings (365
// Admin -> Venue + Refund Policy), so a venue site can never state a term the platform does not apply.
// Anonymous, keyed by the venue's publishable key:
//   GET /api/embed/{key}/policies
// Optional attributes on the mount: data-refund-href (default "/refund-policy").
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, clear, footer, renderLoading } from '../ui';
import { handleFetchFailure } from '../maintenance';

interface PolicySection { key: string; title: string; sentences: string[]; }
interface PublicVenuePolicies {
  venueName: string;
  venueSlug: string;
  sections: PolicySection[];
  platformTermsUrl: string;
  platformPrivacyUrl: string;
  updatedAt?: string | null;
}

const TITLE = 'Terms & policies';

const CSS = `
.e365 .e365-pol h4 { margin: 14px 0 4px; font-size: 1rem; }
.e365 .e365-pol p { margin: 0 0 6px; font-size: .92rem; line-height: 1.5; white-space: pre-line; }
.e365 .e365-pol-links a { margin-right: 14px; }
.e365 .e365-pol-upd { color: var(--e365-muted); font-size: .78rem; margin-top: 10px; }
`;

function updatedLabel(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `Last updated ${d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`;
}

export function mountPolicies(mount: HTMLElement, cfg: EmbedConfig, theme: ThemeOptions, venueKey: string): void {
  const { root, shadow } = createRoot(mount, theme);
  const style = document.createElement('style');
  style.textContent = CSS;
  shadow.insertBefore(style, root);

  const refundHref = mount.getAttribute('data-refund-href') || '/refund-policy';
  const url = `${cfg.baseApiUrl}/api/embed/${encodeURIComponent(venueKey)}/policies`;

  async function load(): Promise<void> {
    renderLoading(root, TITLE);
    let data: PublicVenuePolicies;
    try {
      let res: Response;
      try { res = await fetch(url); }
      catch (e) { const down = await handleFetchFailure(null); if (down) return; throw e; }
      if (res.status === 503 && await handleFetchFailure(res)) return;
      if (!res.ok) throw new Error(String(res.status));
      data = (await res.json()) as PublicVenuePolicies;
    } catch {
      clear(root);
      root.append(
        h('h3', undefined, TITLE),
        h('div', { class: 'e365-alert error' }, 'We couldn’t load the terms and policies just now.'),
      );
      const retry = h('button', { class: 'e365-btn', type: 'button' }, 'Try again');
      retry.addEventListener('click', () => void load());
      root.append(retry, footer());
      return;
    }

    clear(root);
    const box = h('div', { class: 'e365-pol' });
    box.append(h('h3', undefined, `${data.venueName} terms & policies`));
    box.append(h('p', { class: 'e365-muted' },
      'What you agree to when you book or buy here, and how it fits with the 365 Sports Online account you use to pay.'));
    for (const s of data.sections) {
      box.append(h('h4', undefined, s.title));
      for (const line of s.sentences) box.append(h('p', undefined, line));
      if (s.key === 'refunds') {
        const p = h('p', { class: 'e365-pol-links' });
        p.append(h('a', { href: refundHref }, 'Read the full refund policy'));
        box.append(p);
      }
      if (s.key === 'platform') {
        const p = h('p', { class: 'e365-pol-links' });
        p.append(
          h('a', { href: data.platformTermsUrl, target: '_blank', rel: 'noopener' }, '365 Sports Online Terms of Service'),
          h('a', { href: data.platformPrivacyUrl, target: '_blank', rel: 'noopener' }, '365 Sports Online Privacy Policy'),
        );
        box.append(p);
      }
    }
    const upd = updatedLabel(data.updatedAt);
    if (upd) box.append(h('div', { class: 'e365-pol-upd' }, upd));
    root.append(box, footer());
  }

  void load();
}
